import { useState } from 'react';
import { FilterField, SnackbarType, SnackbarProvider, DataTable, Column, useTask, useDateColumn, ModalProvider, useChangeEffect, TextOverflow, useMultiSelect, MultiSelectFilter, SelectFilter, useSelectFilter, useInputFilter, InputFilter, DatepickerFilter, useDatepickerFilter, Filter, DropdownOption, OptionsDropdown, useControlledState, theme, formatDateTime, getFlexStyles, Pagination, DropdownOptionClickEvent, parseNumber, getURLSearchParams } from "vig-ui-library";
import { RequestListingData } from '@/Schemas/Listing/RequestListingData';
import { ListingInput, ListingInputSchema } from '@/Schemas/Listing/ListingInput';
import { ListingOutput, ListingOutputSchema } from '@/Schemas/Listing/ListingOutput';
import { getList, getMyList } from '@/Api/ListingApi';
import { Box, FormControlLabel, Icon, IconButton, LinearProgress, Switch, Typography, useTheme } from '@mui/material';
import { useAppProvider } from '@/Provider/AppProvider';
import { IoDownloadOutline, IoOpenOutline, IoPencilOutline, IoTrashOutline } from 'react-icons/io5';
import { getTextColor } from '@/Enums/RequestStateTypeEnum';
import { PinnedColumn } from 'vig-ui-library/src/components/dataTable/DataTable';
import { GetRequestsStatesInputSchema } from '@/Schemas/RequestForm/GetRequestsStatesInput';
import { GetRequestStateDtoSchema } from '@/Schemas/RequestForm/GetRequestStateDto';
// @ts-ignore
import { useRequestApi } from 'request-form-lib';
import axiosInstance from '@/axios';
import { RequestDetailInputSchema } from '@/Schemas/RequestForm/RequestDetailInput';
import { useRouter } from '@/Router/router';
import { PiDotsThreeOutlineFill } from "react-icons/pi";
import { PermissionType } from '@/Enums/PermissionType';
import { baseURL } from '@/Utils/environment';
import { handleDevelopmentError } from '@/Utils/error';
import { useSearchParams } from 'react-router-dom';

type RequestsOrderByDesc =
    'stateEventDate' |
    'requestTypeId' |
    'stateTypeId' |
    'sourceTypeId' |
    'sourceId' |
    'partner' |
    'partnerId' |
    'partnerEmail' |
    'recordTypeId' |
    'recordId';

type RequestOrderByAsc = `-${RequestsOrderByDesc}`;

type RequestsOrderBy = RequestsOrderByDesc | RequestOrderByAsc;

type RequestTableColumn = Column<RequestListingData, RequestsOrderBy> | PinnedColumn<RequestListingData, RequestsOrderBy>;

export default function List() {
    const theme = useTheme();

    const { openSnackbar } = SnackbarProvider.useSnackbar();

    const reqeustApi = useRequestApi(axiosInstance);

    const { configurations, permissions } = useAppProvider();

    const [searchParams, setSearchParams] = useSearchParams();

    const [showAllRequests, setShowAllRequests] = useState<boolean>(searchParams.get("showAllRequests") === "true");

    const getListingInput = (searchParams: URLSearchParams) => {
        const defaultInput = ListingInputSchema.parse({ order: "-stateEventDate" });
        if (!searchParams.size) return defaultInput;
        const dateToRaw = searchParams.get("dateTo");
        const dateFromRaw = searchParams.get("dateFrom");
        const input = {
            order: searchParams.get("order"),
            page: parseNumber(searchParams.get("page")) || 1,
            limit: parseNumber(searchParams.get("limit")) || 25,
            requestTypes: searchParams.getAll("requestTypes").map(Number),
            dateFrom: dateFromRaw ? new Date(dateFromRaw) : null,
            dateTo: dateToRaw ? new Date(dateToRaw) : null,
            stateTypes: searchParams.getAll("stateTypes").map(Number),
            sourceType: parseNumber(searchParams.get("sourceType")),
            sourceId: parseNumber(searchParams.get("sourceId")),
            partner: searchParams.get("partner") || null,
            partnerId: parseNumber(searchParams.get("partnerId")),
            partnerEmail: searchParams.get("partnerEmail") || null,
            recordType: parseNumber(searchParams.get("recordType")),
            recordId: parseNumber(searchParams.get("recordId")),
            processingTypes: searchParams.getAll("processingTypes").map(Number),
        };
        const parsed = ListingInputSchema.safeParse(input);
        if (parsed.success) return parsed.data;
        return defaultInput;
    };

    const [query, setQuery] = useState<ListingInput>(getListingInput(searchParams));

    const { navigateToPreviewUpdateRequest } = useRouter();

    const getDataTask = useTask(async (newQuery: ListingInput): Promise<ListingOutput> => {
        try {
            if (showAllRequests) return getList(newQuery);
            return getMyList(newQuery);
        } catch (err) {
            handleDevelopmentError(err);
            openSnackbar({
                text: "Nepodarilo sa načítať dáta, skúste to znova. Ak problém pretrváva, kontaktujte IT.",
                type: SnackbarType.ERROR,
            });
            return ListingOutputSchema.parse({ items: [] });
        }
    });

    const cancelRequest = async (request: RequestListingData) => {
        try {
            const input = RequestDetailInputSchema.parse({ requestId: request.id });

            await reqeustApi.cancelRequest(input);

            openSnackbar({ text: "Žiadosť bola zmazaná", type: SnackbarType.SUCCESS });

            getDataTask.debouncedPerform(query);
        } catch (err) {
            handleDevelopmentError(err);
            openSnackbar({ text: "Nepodarilo sa zmazať žiadost.", type: SnackbarType.ERROR });
        }
    }

    useChangeEffect(() => {
        const newQuery = { ...query, page: 1, sourceType: null, sourceId: null }
        setQuery(newQuery);
        getDataTask.perform(newQuery);
    }, [showAllRequests]);

    /// We send current requests displayed in the list to the getRequestsStates every 2 seconds
    /// To check if the state of any of the requests has changed. If so, we update the list
    /// by calling getRequestsStates
    useChangeEffect(() => {
        const interval = setInterval(async () => {
            const requestsStates = getDataTask.value?.items.map((r) => GetRequestStateDtoSchema.parse({
                requestId: r.id,
                requestStateTypeId: r.stateTypeId,
                isSubmitted: r.isSubmitted,
            }));

            const input = GetRequestsStatesInputSchema.parse({ requestsStates })

            const result = await reqeustApi.getRequestsStates(input);

            if (result) getDataTask.perform(query);
        }, 2000);

        return () => clearInterval(interval);
    }, [getDataTask.value]);

    useChangeEffect(() => {
        const newSearchParams = getURLSearchParams(query);
        if (showAllRequests) newSearchParams.append("showAllRequests", "true");
        else newSearchParams.delete("showAllRequests");
        setSearchParams(newSearchParams);
    }, [query]);

    /// ----------------------------------------------------------------------
    /// Columns
    /// ----------------------------------------------------------------------
    const nameColumn: RequestTableColumn = {
        name: "Názov",
        sort: { desc: "requestTypeId", asc: "-requestTypeId" },
        tableCellBuilder: (data) => {
            const foundTypeItem = configurations.type.items.find((rt) => rt.id === data.requestTypeId);
            if (!foundTypeItem) return "Neznámy typ";
            return <TextOverflow fontSize={15} text={foundTypeItem.label} maxWidth={250} title={foundTypeItem.label} />
        },
    };

    const dateColumn: RequestTableColumn = useDateColumn({
        name: "Dátum",
        getDate: (data) => data.stateEventDate || data.createdDate,
        sort: { desc: "stateEventDate", asc: "-stateEventDate" },
        formatter: (date: Date) => formatDateTime(date),
    });

    const stateColumn: RequestTableColumn = {
        name: "Stav",
        width: 270,
        sort: { desc: "stateTypeId", asc: "-stateTypeId" },
        tableCellBuilder: (data) => {
            const foundStateItem = configurations.state.commonItems.find((st) => st.id === data.stateTypeId);
            if (!foundStateItem) return "Neznámy stav";
            return <Box display="flex" alignItems="center" gap={1}>
                <Icon sx={{ fontSize: 24, color: foundStateItem.color }}>{foundStateItem.icon}</Icon>

                <TextOverflow
                    title={foundStateItem.label}
                    variant="body1"
                    maxWidth={240}
                    fontWeight={500}
                    color={foundStateItem.color || (data.stateTypeId ? getTextColor(data.stateTypeId, theme) : theme.palette.text.primary)}
                    text={foundStateItem.label}
                    fontSize={15}
                />
            </Box>
        }
    }

    const reasonColumn: RequestTableColumn = {
        name: "Zdôvodnenie",
        width: 250,
        tableCellBuilder: (data) => {
            const reasoning = data.reasoning || "-";
            const truncatedReasoning = reasoning.length > 50 ? reasoning.substring(0, 50) + "..." : reasoning;
            return <TextOverflow
                text={truncatedReasoning}
                maxWidth={250}
                title={data.reasoning || "-"}
                fontSize={15}
            />;
        },
    };

    const requesterColumn: RequestTableColumn = {
        name: "Podavateľ",
        sort: { desc: "sourceTypeId", asc: "-sourceTypeId" },
        width: 140,
        tableCellBuilder: (data) => {
            const foundSourceItem = configurations.source.items.find((st) => st.id === data.sourceTypeId);
            const label = foundSourceItem?.label || "-";
            return <TextOverflow fontSize={15} text={label} maxWidth={160} />
        },
    }

    const requesterIdColumn: RequestTableColumn = {
        name: "ID podavateľa",
        sort: { desc: "sourceId", asc: "-sourceId" },
        width: 180,
        tableCellBuilder: (data) => <span style={{ whiteSpace: "nowrap" }}>{data.sourceId || "-"}</span>,
    }

    const optionsColumn: RequestTableColumn = {
        name: "",
        tableCellBuilder: (data: RequestListingData) => <OptionsColumn
            {...data}
            cancelRequest={cancelRequest}
            performGetData={() => getDataTask.perform(query)}
        />,
        width: 100,
        pinned: true,
    };

    const clientColumn: RequestTableColumn = {
        name: "Klient",
        width: 140,
        sort: { desc: "partner", asc: "-partner" },
        tableCellBuilder: (data) => <TextOverflow
            text={data.partner || "-"}
            maxWidth={140}
            variant="subtitle2"
            title={data.partner || ""}
            fontSize={15}
        />
    };

    const clientIdColumn: RequestTableColumn = {
        name: "ID klienta",
        width: 130,
        sort: { desc: "partnerId", asc: "-partnerId" },
        tableCellBuilder: (data) => <TextOverflow
            text={data.partnerId?.toString() || "-"}
            maxWidth={130}
            variant="subtitle2"
            title={data.partnerId ? data.partnerId.toString() : ""}
            fontSize={15}
        />
    };

    const clientEmailColumn: RequestTableColumn = {
        name: "Email klienta",
        width: 140,
        sort: { desc: "partnerEmail", asc: "-partnerEmail" },
        tableCellBuilder: (data) => <TextOverflow
            text={data.partnerEmail || "-"}
            maxWidth={140}
            variant="subtitle2"
            title={data.partnerEmail || ""}
            fontSize={15}
        />
    };

    const documentIdColumn: RequestTableColumn = {
        name: "ID dokumentu",
        width: 130,
        tooltip: "Id klienta, číslo poistnej zmluvy alebo číslo škody",
        tableCellBuilder: (data) => <TextOverflow
            text={data.recordId?.toString() || "-"}
            maxWidth={130}
            variant="subtitle2"
            title={data.recordId ? data.recordId.toString() : ""}
            fontSize={15}
        />
    };

    const documentTypeColumn: RequestTableColumn = {
        name: "Typ dokumentu",
        width: 130,
        sort: { desc: "recordTypeId", asc: "-recordTypeId" },
        tableCellBuilder: (data) => {
            const foundRecordItem = configurations.record.items.find((rt) => rt.id === data.recordTypeId);
            if (!foundRecordItem) return "-";
            return <TextOverflow
                text={foundRecordItem.label}
                maxWidth={130}
                title={foundRecordItem.label}
                variant="subtitle2"
                fontSize={15}
            />
        }
    }

    const columns: RequestTableColumn[] = [
        nameColumn,
        dateColumn,
        stateColumn,
        ...(showAllRequests ? [requesterColumn, requesterIdColumn] : []),
        clientColumn,
        clientIdColumn,
        clientEmailColumn,
        reasonColumn,
        documentIdColumn,
        documentTypeColumn,
        optionsColumn,
    ];

    return (
        <Box sx={{ ...getFlexStyles("column", true), height: "100%", maxHeight: "100%", overflow: "hidden" }}>
            <LinearProgress sx={{ visibility: !getDataTask.isInitialCall && getDataTask.isRunning ? "visible" : "hidden" }} />

            {(permissions.has(PermissionType.READ_ALL_REQUESTS)) && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, padding: 2 }}>
                    <FormControlLabel
                        control={<Switch checked={showAllRequests} onChange={() => {
                            const toggledValue = !showAllRequests;
                            setShowAllRequests(toggledValue);
                            if (toggledValue) searchParams.append("showAllRequests", "true");
                            else searchParams.delete("showAllRequests");
                            setSearchParams(searchParams);
                        }} />}
                        label="Zobraziť všetky žiadosti"
                    />
                </Box>
            )}

            <Box sx={{ paddingX: 2, marginTop: !permissions.has(PermissionType.READ_ALL_REQUESTS) ? 2 : 0 }}>
                <RequestListFilters query={query} setQuery={setQuery} showAllRequests={showAllRequests} />
            </Box>

            <Box display="flex" pb={{ sm: 1, md: 2 }} p={2} justifyContent="flex-end">
                <Pagination
                    totalCount={getDataTask.value?.count || 0}
                    pageSizeOptions={[10, 25]}
                    page={query.page}
                    pageSize={query.limit ?? 10}
                    onChange={(page: number, limit: number) => {
                        setQuery({ ...query, page, limit });
                    }}
                />
            </Box>

            <DataTable
                tableMinWidth={1500}
                cellsHeight={53}
                columns={columns}
                // @ts-ignore
                getDataTask={getDataTask}
                query={query}
                setQuery={setQuery}
                paddingLeft={2}
                onRowDoubleClick={(row) => {
                    navigateToPreviewUpdateRequest(row.id, row.requestTypeId?.toString());
                }}
                showLoaderOnlyOnInitialCall
            />
        </Box>
    );
}

type RequestListFiltersProps = {
    query: ListingInput;
    setQuery: React.Dispatch<React.SetStateAction<ListingInput>>;
    showAllRequests: boolean;
}

const RequestListFilters = ({ query, setQuery, showAllRequests }: RequestListFiltersProps) => {
    const { configurations } = useAppProvider();

    const [availableRequestTypes, setAvailableRequestTypes] = useControlledState<number[]>([]);

    const resolveAvailableRequestTypes = (sourceId: number | null) => {
        if (!sourceId) {
            setAvailableRequestTypes([]);
        } else {
            const sourceItem = configurations.source.items.find((configItem) => configItem.id === sourceId);
            setAvailableRequestTypes(sourceItem?.requestTypeConnections ?? []);
        }
    }

    /// ----------------------------------------------------------------------
    /// Filters
    /// ----------------------------------------------------------------------
    const requestTypeFilter: MultiSelectFilter<number> = useMultiSelect({
        options: configurations.type.items
            .filter((_item) => {
                return availableRequestTypes.current.length === 0 || availableRequestTypes.current.includes(_item.id);
            })
            .map((item) => ({ label: item.label, value: item.id })),
        placeholder: "Typ žiadosti",
        onChange: (value: number[]) => {
            setQuery((prevQuery) => ({ ...prevQuery, requestTypes: value }));
        },
        getValue: () => query.requestTypes,
    });

    const dateFrom: DatepickerFilter = useDatepickerFilter({
        placeholder: "Dátum od",
        onChange: (value: Date | null) => {
            setQuery((prevQuery) => ({ ...prevQuery, dateFrom: value }));
        },
        getValue: () => query.dateFrom,
        maxDate: query.dateTo || new Date(),
    });

    const dateTo: DatepickerFilter = useDatepickerFilter({
        placeholder: "Dátum do",
        onChange: (value: Date | null) => {
            setQuery((prevQuery) => ({ ...prevQuery, dateTo: value }));
        },
        getValue: () => query.dateTo,
        minDate: query.dateFrom || undefined,
        maxDate: new Date(),
    });

    const statusFilter: MultiSelectFilter<number> = useMultiSelect({
        options: configurations.state.commonItems.map((item) => ({ label: item.label, value: item.id })),
        placeholder: "Stav žiadosti",
        onChange: (value: number[]) => {
            setQuery((prevQuery) => ({ ...prevQuery, processingTypes: [], stateTypes: value }));
        },
        getValue: () => query.stateTypes,
    });

    const sourceFilter: SelectFilter<number> = useSelectFilter({
        options: configurations.source.items.map((item) => ({ label: item.label, value: item.id })),
        placeholder: "Podavateľ",
        onChange: (value: number | null) => {
            resolveAvailableRequestTypes(value);
            setQuery((prevQuery) => ({
                ...prevQuery,
                sourceType: value,
                requestTypes: availableRequestTypes.current.length > 0
                    ? prevQuery.requestTypes.filter(rt => availableRequestTypes.current.includes(rt))
                    : prevQuery.requestTypes
            }));
        },
        getValue: () => query.sourceType,
    });

    const processingTypesFilter: MultiSelectFilter<number> = useMultiSelect({
        options: configurations.processing.items.map((item) => ({ label: item.label, value: item.id })),
        placeholder: "Spôsob spracovania",
        onChange: (value: number[]) => {
            setQuery((prevQuery) => ({ ...prevQuery, stateTypes: [], processingTypes: value }));
        },
        getValue: () => query.processingTypes,
        tooltipBuilder: () => <Typography variant="body1" color={theme.palette.common.white}>
            Iba odoslané žiadosti sa vedia filtrovať podľa typu spracovania
        </Typography>
    });

    const sourceIdFilter: InputFilter = useInputFilter({
        placeholder: "ID Podavateľa",
        onChange: (value: string) => {
            if (!value) return setQuery((prevQuery) => ({ ...prevQuery, sourceId: null }));
            else setQuery((prevQuery) => ({ ...prevQuery, sourceId: +value }));
        },
        getValue: () => query.sourceId?.toString() || "",
        mostRecentOptionsKey: "source_id"
    });

    const clientFilter: InputFilter = useInputFilter({
        placeholder: "Klient",
        onChange: (value: string) => {
            setQuery((prevQuery) => ({ ...prevQuery, partner: value }));
        },
        getValue: () => query.partner || "",
        mostRecentOptionsKey: "client"
    });

    const clientIdFilter: InputFilter = useInputFilter({
        placeholder: "ID klienta",
        onChange: (value: string) => {
            if (!value) return setQuery((prevQuery) => ({ ...prevQuery, partnerId: null }));
            else setQuery((prevQuery) => ({ ...prevQuery, partnerId: +value }));
        },
        getValue: () => query.partnerId?.toString() || "",
        mostRecentOptionsKey: "client_id"
    });

    const clientEmailFilter: InputFilter = useInputFilter({
        placeholder: "Email klienta",
        onChange: (value: string) => {
            if (!value) return setQuery((prevQuery) => ({ ...prevQuery, partnerEmail: null }));
            else setQuery((prevQuery) => ({ ...prevQuery, partnerEmail: value }));
        },
        getValue: () => query.partnerEmail?.toString() || "",
        mostRecentOptionsKey: "client_email"
    });

    const documentTypeFilter: SelectFilter<number> = useSelectFilter({
        placeholder: "Typ dokumentu",
        options: configurations.record.items.map((item) => ({ label: item.label, value: item.id })),
        onChange: (value: number | null) => {
            setQuery((prevQuery) => ({ ...prevQuery, recordType: value }));
        },
        getValue: () => query.recordType,
    });

    const documentIdFilter: InputFilter = useInputFilter({
        placeholder: "ID dokumentu",
        onChange: (value: string) => {
            if (!value) return setQuery((prevQuery) => ({ ...prevQuery, recordId: null }));
            else setQuery((prevQuery) => ({ ...prevQuery, recordId: +value }));
        },
        getValue: () => query.recordId?.toString() || "",
        mostRecentOptionsKey: "document_id"
    });

    const filters: FilterField[] = [
        requestTypeFilter,
        dateFrom,
        dateTo,
        statusFilter,
        ...(showAllRequests ? [sourceFilter, sourceIdFilter, processingTypesFilter] : []),
        clientFilter,
        clientIdFilter,
        clientEmailFilter,
        documentTypeFilter,
        documentIdFilter,
    ];

    return <Filter filters={filters} />
}

type OptionsColumnProps = RequestListingData & {
    cancelRequest: (row: RequestListingData) => Promise<void>;
    performGetData: () => void;
}

const OptionsColumn = ({ cancelRequest, performGetData, ...data }: OptionsColumnProps) => {
    const { openConfirmModal } = ModalProvider.useModal();

    const theme = useTheme();

    const { isMy: isMyRequest } = data;

    const { navigateToPreviewUpdateRequest } = useRouter();

    const confirmCancelRequest = () => {
        openConfirmModal({
            title: "Naozaj si želáte žiadosť odstrániť?",
            description: "Žiadosť bude odstránená a zmeny nebudú vykonané.",
            confirmText: "Odstrániť",
            onConfirm: async () => await cancelRequest(data)
        });
    }

    const openRequest = (e: DropdownOptionClickEvent) => {
        e.stopPropagation();
        navigateToPreviewUpdateRequest(data.id, data.requestTypeId?.toString());
    }

    const options: DropdownOption[] = [
        {
            label: data.isSubmitted || !isMyRequest ? "Otvoriť žiadosť" : "Upraviť žiadosť",
            icon: data.isSubmitted || !isMyRequest ? <IoOpenOutline size={24} /> : <IoPencilOutline size={24} />,
            onClick: openRequest,
        },

    ];

    if (data.isSubmitted && data.generatedDocumentId) options.push({
        label: "Stiahnuť dokument žiadosti",
        icon: <IoDownloadOutline size={24} />,
        onClick: (e) => {
            e.stopPropagation();
            window.open(`${baseURL}/api/request/streamfile?id=${data.generatedDocumentId}&download=true`, "_blank");
        }
    })

    if (!data.isSubmitted && isMyRequest) options.push({
        label: "Odstrániť žiadosť",
        icon: <IoTrashOutline color={theme.palette.error.main} size={24} />,
        onClick: (e) => {
            e.stopPropagation();
            confirmCancelRequest();
        }
    });

    const getIconButton = (option: DropdownOption, index: number): React.ReactNode => {
        return <IconButton key={index + 1} onClick={option.onClick}>
            {option.icon}
        </IconButton>
    };

    const displayDropdown = options.length > 2;

    const iconButtons = displayDropdown ? [options[0]] : options;

    return (
        <Box onClick={(e) => e.stopPropagation()} display="flex" alignItems="center" justifyContent="flex-end">
            {iconButtons.map(getIconButton)}

            {displayDropdown && (
                <OptionsDropdown
                    options={options}
                    trigger={(open) => {
                        return <IconButton onClick={(e) => {
                            e.stopPropagation();
                            open(e.currentTarget);
                        }}>
                            <PiDotsThreeOutlineFill />
                        </IconButton>
                    }}
                />
            )}
        </Box>
    );
};
